import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableRowLink } from '@/components/common/table'
import { ArrowIcon } from '@/components/icons/arrow'
import { routes } from '@/config/routes'
import { PAGE_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useFilteredVaults } from '@/hooks/useFilteredVaults'
import { useSortedVaults } from '@/hooks/useSortedVaults'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import { useVaultsSortState } from '@/hooks/useVaultsSortState'
import type { PositionedVaultsListElement } from '@/hooks/useVaultsWithPositions'
import type { VaultApyRange } from '@/types/apy'
import type { Vault } from '@/types/vault'
import { type VaultsTableColumn, vaultsTableColumns } from '@/types/vaultsTableColumn'
import { formatApyTimestamp, getApyRangeCopy, getApyValue, getCappedApyCopy } from '@/utils/apy'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { shouldShowNotRatedReputation } from '@/utils/reputationRating'
import { getValueColor } from '@/utils/styles/getValueColor'
import { LAZY_SUMMER_PROTOCOL, LIDO_PROTOCOL } from '@vaultsfyi/common/constants'
import { formatAmount, formatUSD } from '@vaultsfyi/common/utils'
import { memo, useEffect } from 'react'
import { Link, generatePath } from 'react-router'
import { ApyTooltip } from '../common/apyTooltip'
import { Button } from '../common/button'
import { CapacityTooltip } from '../common/capacityTooltip'
import { Card } from '../common/card'
import { CategoryTooltip } from '../common/categoryTooltip'
import { NetworkImage } from '../common/networkImage'
import { ProtocolImage } from '../common/protocolImage'
import { ReputationTooltip } from '../common/reputationTooltip'
import { ScrollArea, ScrollBar } from '../common/scrollArea'
import { TokenImage } from '../common/tokenImage'
import { Tooltip, TooltipContent } from '../common/tooltip'
import { VaultFlagsTooltip } from '../common/vaultFlagsTooltip'
import { SearchIcon } from '../icons/search'
import { Content } from '../layout/wrappers'
import { CuratorInfo } from '../shared/curatorInfo'
import { PositionBadge } from '../shared/positionBadge'
import { TableSortButton, type TableSortButtonProps, TableSortTooltipButton } from '../shared/tableSortButton'
import type { VaultPresetsProps } from '../shared/vaultPresets'
import { VaultsFilters } from '../shared/vaultsFilters'
import { DiscoverNavigation } from './discoverNavigation'
import { VaultsPagination } from './vaultsPagination'

const VAULTS_PER_PAGE = 30

const CAPACITY_TOOLTIP_EXCEPTIONS_LIST = [LIDO_PROTOCOL.name, LAZY_SUMMER_PROTOCOL.name]

interface VaultRowProps {
  vault: PositionedVaultsListElement
  apyRange: VaultApyRange
  includeRewards: boolean
}

const VaultRow = memo(function VaultRow({ vault, apyRange, includeRewards }: VaultRowProps) {
  const { trackEvent } = useMixpanel()
  const isReputationNotRated = shouldShowNotRatedReputation(vault)
  const {
    asset,
    network,
    protocol,
    curator,
    holdersData,
    tvl,
    apy,
    tags,
    rewards,
    additionalIncentives,
    apyComposite,
    name,
    balanceUsd,
    lastUpdateTimestamp,
    maxCapacity,
    remainingCapacity,
    flags,
  } = vault
  const protocolDisplayName = getProtocolDisplayName(protocol)

  return (
    <TableRow className="group">
      <TableCell role="rowheader" className="h-14">
        <div className="flex items-center gap-2">
          <TokenImage symbol={asset.symbol} className="size-8 p-1 group-even:bg-page" />
          <div>
            <div className="text-sm">{asset.symbol}</div>
            <div className="flex items-center gap-1 text-xs">
              <NetworkImage network={network.name} className="size-4 group-even:bg-page" />
              <div className="whitespace-nowrap capitalize">{getNetworkName(network.name)}</div>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="px-1">
        <Tooltip tabIndex={-1} className="relative z-[1]">
          <ProtocolImage protocol={protocol} className="size-8 p-1 group-even:bg-page" />
          <TooltipContent>
            <div className="flex items-center gap-1">
              {protocol.protocolUrl ? (
                <Button
                  variant="default"
                  size="default"
                  className="inline-flex items-center gap-1 rounded-full text-base underline"
                  asChild
                >
                  <Link to={protocol.protocolUrl} target="_blank" rel="noreferrer">
                    <ProtocolImage protocol={protocol} className="size-6" />
                    <span>{protocolDisplayName}</span>
                  </Link>
                </Button>
              ) : (
                <div className="inline-flex items-center gap-1">
                  <ProtocolImage protocol={protocol} className="size-6" />
                  <span className="text-base">{protocolDisplayName}</span>
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <span className="max-w-full truncate">{name}</span>
          <PositionBadge balanceUsd={balanceUsd} fadable />
        </div>
        {curator && <CuratorInfo curator={curator} />}
      </TableCell>
      <TableCell>
        <div className="flex items-center">
          {tags.map((tag) => (
            <CategoryTooltip key={tag} category={tag} tabIndex={-1} />
          ))}
        </div>
      </TableCell>
      <TableCell className="px-1">
        <ReputationTooltip
          score={vault.score?.vaultScore}
          isNotRated={isReputationNotRated}
          className="relative z-[1] flex items-center justify-center before:absolute before:h-8 before:w-full"
        />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {holdersData.totalCount ? formatAmount(holdersData.totalCount) : '--'}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <div className="ml-auto grid w-fit grid-cols-[auto,4.5rem] items-center justify-end gap-1">
          {maxCapacity && remainingCapacity && !CAPACITY_TOOLTIP_EXCEPTIONS_LIST.includes(vault.protocol.name) && (
            <CapacityTooltip
              tabIndex={-1}
              maxCapacity={maxCapacity}
              remainingCapacity={remainingCapacity}
              assetSymbol={asset.symbol}
              assetDecimals={asset.decimals}
            />
          )}
          <span className="[&:only-child]:col-span-full">{formatUSD(tvl.usd)}</span>
        </div>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <div className="grid grid-cols-[auto,4.125rem] items-center justify-end gap-1">
          <p
            className={cn('font-semibold', getValueColor(getApyValue({ apy, apyRange, includeRewards, apyComposite })))}
          >
            {getCappedApyCopy({ apy, apyRange, includeRewards, apyComposite })}
            <span className="hidden"> (Yield last updated: {formatApyTimestamp(lastUpdateTimestamp)})</span>
          </p>
          <ApyTooltip
            tabIndex={-1}
            asset={asset}
            apy={apy}
            additionalIncentives={additionalIncentives}
            apyRange={apyRange}
            rewards={rewards}
            apyComposite={apyComposite}
            includeRewards={includeRewards}
            className="relative z-[1]"
          />
        </div>
      </TableCell>
      <TableCell className="px-1">
        <div className="ml-auto flex items-center gap-1">
          <VaultFlagsTooltip flags={flags} />
        </div>
      </TableCell>
      <TableCell className="!pl-1 !pr-3">
        <TableRowLink
          to={generatePath(routes.opportunity, { vaultId: vault.vaultId, network: network.name })}
          title={name}
          prefetch="intent"
          onClick={() => trackEvent('Vaults table row clicked')}
          className="focus-visible:before:rounded-xl focus-visible:before:ring focus-visible:before:ring-inset"
        >
          <ArrowIcon />
        </TableRowLink>
      </TableCell>
    </TableRow>
  )
})

interface Props {
  vaults: Vault[]
  className?: string
}

export function VaultsTable({ vaults, presets, activePreset, onPresetClick, className }: Props & VaultPresetsProps) {
  const { trackEvent } = useMixpanel()

  const {
    searchFor,
    selectedNetworks,
    selectedTokens,
    selectedProtocols,
    selectedCurators,
    selectedTvlRange,
    selectedTags,
    page,
    includeRewards,
    touchedParamsCount,
    apyRange,
    onlyTransactional,
    minVaultScore,
    clearSearchState,
    updateSearchState,
  } = useVaultsSearchState()
  const { sortState, handleSetSortBy } = useVaultsSortState()
  const filteredVaults = useFilteredVaults({
    vaults,
    searchFor,
    selectedNetworks,
    selectedTokens,
    selectedProtocols,
    selectedCurators,
    selectedTags,
    selectedTvlRange,
    onlyTransactional,
    minVaultScore,
  })
  const totalPages = Math.max(1, Math.ceil(filteredVaults.length / VAULTS_PER_PAGE))
  const effectivePage = Math.min(page, totalPages)

  useEffect(() => {
    if (page !== effectivePage) {
      updateSearchState([PAGE_KEY, effectivePage])
    }
  }, [page, effectivePage, updateSearchState])

  const firstItemOnPageIndex = (effectivePage - 1) * VAULTS_PER_PAGE
  const lastItemOnPageIndex = firstItemOnPageIndex + VAULTS_PER_PAGE
  const sortedVaults = useSortedVaults({ vaults: filteredVaults, apyRange, includeRewards, ...sortState })
  const pagedVaults = sortedVaults.slice(firstItemOnPageIndex, lastItemOnPageIndex)

  return (
    <ScrollArea className={cn('h-full', className)}>
      <Content>
        <div className="flex flex-col gap-4 md:gap-6">
          <VaultsFilters
            presets={presets}
            vaults={vaults}
            numberOfVaults={filteredVaults.length}
            activePreset={activePreset}
            onPresetClick={onPresetClick}
            onClearClick={clearSearchState}
            areParamsModified={touchedParamsCount > 0}
            leadingContent={<DiscoverNavigation activePage="vaults" />}
          />
          <Card as="section" className="w-full">
            <ScrollArea type="auto" className="w-full">
              <ScrollBar orientation="horizontal" className="top-0" />
              <Table className="min-w-[976px] table-fixed">
                <TableHeader>
                  <TableRow variant="header">
                    {vaultsTableColumns.map((column) =>
                      getColumnHeaderElement({
                        name: column,
                        apyRange,
                        active: sortState.sortBy === column,
                        asc: sortState.asc,
                        onClick: () => {
                          handleSetSortBy(column)
                          trackEvent('Vaults table column clicked', {
                            column: column,
                            sortBy: sortState.sortBy,
                            asc: sortState.asc,
                          })
                        },
                      })
                    )}
                    <TableHead className="!pl-1 !pr-3 w-9" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedVaults.length > 0 ? (
                    pagedVaults.map((vault) => (
                      <VaultRow
                        key={`vault-row-${vault.network.name}-${vault.vaultId}`}
                        vault={vault}
                        apyRange={apyRange}
                        includeRewards={includeRewards}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={vaultsTableColumns.length + 1} className="text-center">
                        <div className="flex w-full flex-col items-center justify-center gap-4 py-4">
                          <SearchIcon size={48} />
                          <p className="font-bold text-xl">No results for selected filters</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            {filteredVaults.length > 0 && (
              <VaultsPagination
                currentPage={effectivePage}
                itemsCount={filteredVaults.length}
                itemsPerPage={VAULTS_PER_PAGE}
                className="border-t"
              />
            )}
          </Card>
        </div>
      </Content>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  )
}

interface ColumnHeaderProps extends TableSortButtonProps {
  name: VaultsTableColumn
  apyRange: VaultApyRange
}

function getColumnHeaderElement({ name, apyRange, ...props }: ColumnHeaderProps) {
  switch (name) {
    case 'name':
      return (
        <TableHead key={name}>
          <TableSortButton className="capitalize" {...props}>
            {name}
          </TableSortButton>
        </TableHead>
      )
    case 'category':
      return (
        <TableHead key={name} className="w-24 capitalize">
          {name}
        </TableHead>
      )
    case 'protocol':
      return (
        <TableHead key={name} className="w-[88px] px-1">
          <TableSortButton className="capitalize" {...props}>
            {name}
          </TableSortButton>
        </TableHead>
      )
    case 'score':
      return (
        <TableHead key={name} className="w-[92px] px-1">
          <TableSortTooltipButton
            className="capitalize"
            tooltipContent={
              <p>
                Our measure of how battle-tested this vault is, based on its track record, protocol, holders, and
                underlying token. It does not assess network security. Lower scores mean less history, not necessarily
                higher risk.
              </p>
            }
            {...props}
          >
            {name}
          </TableSortTooltipButton>
        </TableHead>
      )
    case 'holders':
      return (
        <TableHead key={name} className="w-[100px] text-right">
          <TableSortButton className="ml-auto capitalize" {...props}>
            {name}
          </TableSortButton>
        </TableHead>
      )
    case 'status':
      return <TableHead key={name} className="w-8 px-1" />
    case 'tvl':
      return (
        <TableHead key={name} className="text-right">
          <TableSortTooltipButton
            className="ml-auto uppercase"
            tooltipContent={<p>The value of deposits currently held in this vault.</p>}
            {...props}
          >
            {name}
          </TableSortTooltipButton>
        </TableHead>
      )
    case 'apy':
      return (
        <TableHead key={name} className="w-[188px] text-right">
          <TableSortTooltipButton
            className="mx-12 ml-auto uppercase"
            tooltipContent={
              <p>
                Annualized return over the selected historical period (1/7/30 days), based on the vault's share price
                change and any rewards accrued.
              </p>
            }
            {...props}
          >
            Yield ({getApyRangeCopy(apyRange).compact})
          </TableSortTooltipButton>
        </TableHead>
      )
    case 'token':
    default:
      return (
        <TableHead key={name} className="w-[168px]">
          <TableSortButton className="capitalize" {...props}>
            {name}
          </TableSortButton>
        </TableHead>
      )
  }
}
